import type { ReactNode } from 'react';
import type { OAuthProviderSlug } from '@tabita/contracts';

/**
 * One badge per provider — a brand-colour monogram, not a reproduction of
 * each company's actual logotype. A hand-drawn Google "G" or GitHub octocat
 * risks coming out subtly wrong; a solid-colour badge with a letter next to
 * the provider's own name (every button pairs the two) reads just as
 * clearly and can't render as a broken shape.
 */
const SIZE = 20;

function Badge({ color, children }: { color: string; children: ReactNode }): ReactNode {
  return (
    <svg width={SIZE} height={SIZE} viewBox="0 0 24 24" aria-hidden="true">
      <rect width="24" height="24" rx="5" fill={color} />
      {children}
    </svg>
  );
}

function Letter({ children }: { children: string }): ReactNode {
  return (
    <text
      x="12"
      y="17"
      textAnchor="middle"
      fontSize="13"
      fontWeight="700"
      fontFamily="system-ui, sans-serif"
      fill="white"
    >
      {children}
    </text>
  );
}

export const OAUTH_ICONS: Record<OAuthProviderSlug, () => ReactNode> = {
  google: () => (
    <Badge color="#4285F4">
      <Letter>G</Letter>
    </Badge>
  ),
  microsoft: () => (
    <svg width={SIZE} height={SIZE} viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="3" width="8.5" height="8.5" fill="#F25022" />
      <rect x="12.5" y="3" width="8.5" height="8.5" fill="#7FBA00" />
      <rect x="3" y="12.5" width="8.5" height="8.5" fill="#00A4EF" />
      <rect x="12.5" y="12.5" width="8.5" height="8.5" fill="#FFB900" />
    </svg>
  ),
  apple: () => (
    <Badge color="#000000">
      <Letter>A</Letter>
    </Badge>
  ),
  facebook: () => (
    <Badge color="#1877F2">
      <Letter>f</Letter>
    </Badge>
  ),
  github: () => (
    <svg width={SIZE} height={SIZE} viewBox="0 0 24 24" aria-hidden="true">
      <rect width="24" height="24" rx="5" fill="#181717" />
      <path
        d="M9.5 8L5.5 12L9.5 16M14.5 8L18.5 12L14.5 16"
        fill="none"
        stroke="white"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  linkedin: () => (
    <Badge color="#0A66C2">
      <Letter>in</Letter>
    </Badge>
  ),
};
